import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  CssBaseline,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ProducerTab from "./components/tabs/ProducerTab";
import AutomatorTab from "./components/tabs/AutomatorTab";
import SpeedShopTab from "./components/tabs/SpeedShopTab";
import AchievementTab from "./components/tabs/AchievementTab";
import { GameContext } from "./provider/GameProvider";
import OptionsTab from "./components/tabs/OptionsTab";
import { tabsMenuSize } from "./Theme";
import { MoleculeTab } from "./components/tabs/MoleculeTab";
import StatisticsTab from "./components/tabs/StatisticsTab";
import LoreTab from "./components/tabs/LoreTab";
import { DialogContext } from "./provider/DialogProvider";
import { getUid } from "./auth/user";
import { buyAllUntil10 } from "./lib/calculator";
import { set } from "lodash";
import SupporterShop from "./components/tabs/SupporterShopTab";
import { Question } from "@phosphor-icons/react/dist/ssr";
import { PopupContext } from "./provider/PopupProvider";
import {
  loadObjectFromLocalStorage,
  saveObjectToLocalStorage,
} from "./save/localSave";
import ChangelogTab from "./components/tabs/ChangelogTab";

const enum TabPanelType {
  Producer = 0,
  Molecule = 1,
  Automator = 2,
  SpeedShop = 3,
  Achievement = 4,
  Options = 5,
  Statistics = 6,
  Lore = 7,
  SupporterShop = 8,
  Changelog = 9,
}

const a11yProps = (index: any) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
  value: index,
});
let isKeyPressed = false;
function App() {
  const [tabValue, setTabValue] = React.useState(TabPanelType.Producer);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      setZoom(window.innerWidth <= 600 ? 1 : 1);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call the function initially to set the zoom based on the initial window size

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const { game, setGame, setManualAutoCollect } = React.useContext(GameContext);
  const { setCheatDialogOpen, setHelpDialogOpen } =
    React.useContext(DialogContext);
  const { showPopup } = React.useContext(PopupContext);

  useEffect(() => {
    if (loadObjectFromLocalStorage("showHoldCTipp")) {
      showPopup("Hold 'C' to collect");
      saveObjectToLocalStorage("showHoldCTipp", false);
    }
  }, [game, showPopup]);

  useEffect(() => {
    const handleKeyDown = (event: { key: string }) => {
      if ((event.key === "p" || event.key === "P") && getUid()) {
        setCheatDialogOpen(true);
      }
      if ((event.key === "b" || event.key === "B") && !isKeyPressed) {
        isKeyPressed = true;
        buyAllUntil10(game.producers, setGame);
      }
      if (event.key === "c" || event.key === "C") {
        setManualAutoCollect(true);
      }
    };
    const handleKeyUp = (event: { key: string }) => {
      if (event.key === "b" || event.key === "B") {
        isKeyPressed = false;
      }
      if (event.key === "c" || event.key === "C") {
        setManualAutoCollect(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [game.producers, setGame, setCheatDialogOpen]);

  const moleculeUnlocked = game.molecule.unlocked;
  const speedShopUnlocked =
    game.strengtheningBoost.upgradeLevel >= 1 ||
    game.unlockedAchievementIds.has(13);

  const tabs = [
    { label: "Producers", index: TabPanelType.Producer },
    {
      label: "Molecules",
      index: TabPanelType.Molecule,
      condition: moleculeUnlocked,
      color: "darkorange",
    },
    { label: "Automators", index: TabPanelType.Automator },
    {
      label: "Speed-Shop",
      condition: speedShopUnlocked,
      index: TabPanelType.SpeedShop,
    },
    { label: "Achievements", index: TabPanelType.Achievement },
    { label: "Options", index: TabPanelType.Options },
    { label: "Statistics", index: TabPanelType.Statistics },
    { label: "Lore", index: TabPanelType.Lore },
    //{ label: "Shop", index: TabPanelType.SupporterShop },
    { label: "Changelog", index: TabPanelType.Changelog },
  ];

  return (
    <div
      style={{
        transform: `scale(${zoom})`,
        transformOrigin: "top left",
        width: "100vw",
        height: "100vh",
      }}
    >
      <div
        style={{
          color: "white",
          position: "fixed",
          bottom: 20,
          left: 20,
          zIndex: 100,
        }}
      >
        Newest Updates:{" "}
        <a
          style={{ color: "lightblue" }}
          href="https://www.reddit.com/r/EvolutionXpl/"
          target="_blank"
        >
          r/EvolutionXpl
        </a>
      </div>
      <IconButton
        style={{
          position: "fixed",
          top: `calc(100vh - 100px)`,
          left: `calc(100vw - 80px)`,
          //top: 400,
          zIndex: 100,
        }}
        onClick={() => setHelpDialogOpen(true)}
      >
        <Question color="white" size={48} />
      </IconButton>
      <div style={{ overflow: "hidden" }}>
        <CssBaseline />
        <div style={{ backgroundColor: "green" }}>
          <Tabs
            value={tabValue}
            onChange={(event, newValue: number) => setTabValue(newValue)}
            orientation="vertical"
            style={{
              position: "absolute",
              top: 40,
              left: 0,
              backgroundColor: "#111111",
              borderColor: "lightblue",
              borderWidth: "1px",
              borderStyle: "solid",
              padding: 8,
              paddingLeft: 0,
              width: tabsMenuSize,
              zIndex: 2,
            }}
            TabIndicatorProps={{
              style: {
                backgroundColor:
                  tabValue === TabPanelType.Molecule ? "orange" : undefined,
              },
            }}
          >
            {tabs.map((tab) =>
              tab.condition === undefined || tab.condition ? (
                <Tab
                  key={tab.index}
                  label={tab.label}
                  style={{
                    color: tabValue !== tab.index ? "white" : tab.color,
                  }}
                  {...a11yProps(tab.index)}
                />
              ) : undefined
            )}
          </Tabs>
        </div>
        <Container
          style={{
            position: "absolute",
            left: tabsMenuSize + 0,
            right: 0,
            overflowX: "hidden",
            maxWidth: `calc(100% - ${tabsMenuSize + 0}px)`,
            height: "100%",
          }}
        >
          <CustomTabPanel value={tabValue} index={TabPanelType.Producer}>
            <ProducerTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Molecule}>
            <MoleculeTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Automator}>
            <AutomatorTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.SpeedShop}>
            <SpeedShopTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Achievement}>
            <AchievementTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Options}>
            <OptionsTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Statistics}>
            <StatisticsTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Lore}>
            <LoreTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.SupporterShop}>
            <SupporterShop />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={TabPanelType.Changelog}>
            <ChangelogTab />
          </CustomTabPanel>
        </Container>
      </div>
    </div>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }} hidden={value !== index}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default App;
